// services/ViewService.js
import axios from "axios";
import UserService from "./UserService";
import ConfigurationService from "./ConfigurationService";

const baseUrl = ConfigurationService.resolveApi().rest;
const ENGAGEMENT_DEFAULT_VIEW_KEY = "engagementDefaultViewId";

const ViewService = {
  /**
   * Toggle subscription on a view.
   * @param {Object} params
   * @param {number} params.viewId - The view ID.
   * @param {boolean} [params.subscribe=true] - Set true to subscribe, false to unsubscribe.
   */
  async subscribe({ viewId, subscribe = true }) {
    const { userId, authToken } = UserService.getUserData();

    try {
      const response = await axios.post(
        `${baseUrl}/view/${viewId}/subscribe`,
        { userId, subscribe },
        { headers: { Authorization: authToken } }
      );
      return response.data;
    } catch (error) {
      console.error("Error setting subscription state", error);
      throw error;
    }
  },
   /**
   * Get the subscription status for a view for the current user.
   * @param {Object} params - object with viewId.
   * @returns {boolean} isSubscribed
   */
   async getSubscriptionStatus({ viewId }) {
    const { userId, authToken } = UserService.getUserData();
    try {
      const response = await axios.get(`${baseUrl}/view/${viewId}/subscriptionStatus`, {
        headers: { Authorization: authToken },
        params: { userId },
      });
      return response.data.isSubscribed;
    } catch (error) {
      console.error("Error retrieving subscription status", error);
      return false;
    }
  },
  /**
   * Checks local storage for a default view ID and finds the matching object
   * Returns the view object if found, or 'INVALID' if a key existed but no view matched.
   */
  getStoredEngagementView({ customViews, defaultViews }) {
    const storedId = window.localStorage.getItem(ENGAGEMENT_DEFAULT_VIEW_KEY);
    if (!storedId) return null;

    const customView = customViews.find((v) => String(v.id) === String(storedId));
    if (customView) return { view: customView, type: 'custom' };

    const defaultView = defaultViews.find((v) => String(v.id) === String(storedId));
    if (defaultView) return { view: defaultView, type: 'default' };

    // ID exists in storage but matches nothing (clean up)
    window.localStorage.removeItem(ENGAGEMENT_DEFAULT_VIEW_KEY);
    return 'INVALID';
  },

  /**
   * Helper to resolve the View ID from either a direct ID or a complex response object
   */
  resolveViewId({ viewId, viewObject }) {
    if (viewId) return viewId;
    if (!viewObject) return null;

    if (viewObject.id) return viewObject.id;

    if (viewObject.data) {
      if (typeof viewObject.data === "number" || typeof viewObject.data === "string") return viewObject.data;
      if (viewObject.data.id) return viewObject.data.id;
      if (viewObject.data.view && viewObject.data.view.id) return viewObject.data.view.id;
    }

    if (viewObject.view && viewObject.view.id) return viewObject.view.id;

    return null;
  },
  /**
   * logic to save or remove the default view based on the checkbox
   */
  updateStoredEngagementView({ viewId, isDefault, viewObject = null }) {
    const effectiveId = this.resolveViewId({ viewId, viewObject });
    const storedId = window.localStorage.getItem(ENGAGEMENT_DEFAULT_VIEW_KEY);

    if (isDefault && effectiveId) {
      window.localStorage.setItem(ENGAGEMENT_DEFAULT_VIEW_KEY, String(effectiveId));
    } else if (!isDefault && effectiveId && storedId === String(effectiveId)) {
      window.localStorage.removeItem(ENGAGEMENT_DEFAULT_VIEW_KEY);
    }
  },

  /**
   * Cleanup when a view is deleted
   */
  clearStoredViewIfMatches({ viewId }) {
      const storedId = window.localStorage.getItem(ENGAGEMENT_DEFAULT_VIEW_KEY);
      if (storedId && storedId === String(viewId)) {
          window.localStorage.removeItem(ENGAGEMENT_DEFAULT_VIEW_KEY);
      }
  }
};

export default ViewService;
